
import { Type } from "@angular/core";
import { BgPositionEnum, BloglistLayoutEnum, TestimonialLayoutEnum, ContentElement, GridCell, GridLayoutEnum, HeaderConfig, OrientationEnum, Product, RowCell, HeroLayoutEnum, VerticalAlignEnum, RichTextOptions } from "@urbannex/storethemes";
import { ThemeConfig } from "@urbannex/storethemes";
import { FooterConfig } from "@urbannex/storethemes";
export enum InputLevel {
    PAGE = "Page",
    SECTION = "Section",
}

export enum PageType {
    HOME = "Home",
    ABOUT_US = "About Us",
    PRODUCT = "Product",
    PRODUCT_CATEGORY = "Product Category",
    CART = "Cart",
    BLOG = "Blog",
    CONTACT_US = "Contact Us",
    STORE_POLICIES = "Store Policies",
    FAQS = "Frequently Asked Questions",
    BLOGROOT = "Blog Root",
    SUPPORTROOT = "Support Root"
}

export enum PageLevel {
    PRIMARY = "Primary",
    SECONDARY = "Secondary"
}

export enum LayoutType {
    GRID = "Grid",
    ROW = "Row",
    BANNER = "Banner",
    BLOCK = "Block",
    SLIDER = "Slider",
    LIST = "List",
}

export enum ContentType {
    PRODUCT_SUMMARY = "Product Summary",
    PRODUCT_LIST = "Product List",
    PRODUCT_DETAIL = "Product Detail",
    BLOG_LIST = "Blog List",
    TESTIMONIALS = "Testimonials",
    BLOG_ARTICLE = "Blog Article",
    RICH_TEXT = "Rich Text Block",
    BRANDS = "Brands",
    FORM = "Form"
}

export enum RendererType {
    GRID = "grid",
    ROW = "row",
    BANNER = "banner",
    BLOGLIST = "bloglist",
    NEWSLETTER = "news-letter-sign-up",
    STOREFEATURES = "store-features",
    SLIDER = "slider",
    RICH_TEXT = "rich-text",
    TESTIMONIALS = "testimonials",
    CATEGORY = "category",
    PRODUCT = "product",
    CART = "cart",
    BLOG = "blog",
    HEROIMAGE = "hero-image"
}

export enum FeatureType {
    DELIVERY = "delivery",
    RETURNS = "returns",
    SECURITY = "security",
    SUPPORT = "support"
}

export enum SliderType {
    ROW = "row",
    TABBED = "tabbed",
    SHOW = "show",
    EYE = "eye"
}

export interface SectionData {
    borderColor?: string;
    rendererType?: RendererType,
    title?: ContentElement,
    heading?: string,
    bannerOptions?: any,
    imageOrientation?: OrientationEnum,
    width?: number,
    height?: number,
    marginBottom?: number,
    testimonialOptions?: any,
    richTextOptions?: RichTextOptions,
    layoutClass?: BloglistLayoutEnum | GridLayoutEnum | HeroLayoutEnum | TestimonialLayoutEnum,
    cellOrientation?: OrientationEnum,
    blogListOptions?: any,
    gridOptions?: any,
    rowCells?: any[],
    numberOfElements?: number,
    rowOptions?: any,
    isPreview?: boolean,
    selectedCell?: GridCell | RowCell;
    product?: Product;
    wrapperClass?: string,
    bgPosition?: BgPositionEnum,
    bgColor?: string,
    sliderType?: SliderType,
    cellHeight?: string,
    verticalAlign?: VerticalAlignEnum,
    imgSrc?: string,
    borderWidth?: string,
    borderRadius?: string,
    color?: string
}
/**
 * ContentStatusEnum
 * In Progress - Content is being created or edited (used for new content on the client side only)
 * Initial Draft - First draft of the content is completed (when first saved to the backend and ready for review)
 * Revised Draft - Content has been revised based on feedback (used when content is sent back for revisions)
 * Final Draft - Content is finalized and ready for review (used when content is ready for final approval)
 * Published - Content is live and published
 * Deactivated - Content is no longer active or published
 */

export enum ContentStatusEnum {
    CTNT_IN_PROGRESS = "In Progress",
    CTNT_INITIAL_DRAFT = "Initial Draft",
    CTNT_REVISED_DRAFT = "Revised Draft",
    CTNT_FINAL_DRAFT = "Final Draft",
    CTNT_PUBLISHED = "Published",
    CTNT_DEACTIVATED = "CTNT_DEACTIVATED"
}

export interface Section {
    title?: ContentElement,
    contentType?: ContentType;
    sectionId?: string;
    index?: number;
    rendererType?: RendererType;
    status?: ContentStatusEnum;
    sectionData?: SectionData;
    sectionTemplate?: Type<any>;
}

/* export interface Page {
    pageId?:string;
    pageName?:string;
    pageType?:PageType;
    pageLevel?:PageLevel;
    sections?:Section[];
    pageTemplate?:Type<any>
} */

export interface Site {
    siteName?: string;
    siteId?: string;
    siteHeader?: HeaderConfig;
    theme?: ThemeConfig;
    pages?: Page[];
    siteFooter?: FooterConfig;
}

export interface SiteHeader {
    topHeader?: HeaderSection;
    mainHeader?: HeaderSection;
    bottomHeader?: HeaderSection;
}

export interface HeaderSection {
    status?: HeaderSectionEnableEnum;
    cells?: HeaderCell[];
}

export enum HeaderSectionEnableEnum {
    ENABLED = "enabled",
    DISABLED = "disabled"
}

export interface HeaderCell {
    cellId?: string;
}

export interface TextStyles {
    style?: CSSStyleDeclaration
}

export interface SelectOption {
    description: string;
    key: string;
}
export interface FieldIcon {
    icon: string,
    pack: string
}

export interface PopoverData {
    title: string,
    popoverType: string,
    target: string,
    property?: string, // Property to be updated in the content element, used for color settings for spefici properties like backgoround and border
    service?: string
}

export interface EditorInputField {
    name: string,
    title: string,
    type: string,
    controlType: string,
    fromField?: string, // Field from which the field value is taken,
    value?: any,
    context?: any,
    readonly?: boolean,
    fieldIcon?: FieldIcon,
    selectOptions?: SelectOption[],
    popoverData?: PopoverData
}

export interface PageOptions {
    pageId?: string;
    pageName?: string;
    pageType?: PageType;
    pageLevel?: PageLevel;
    sections?: Section[];
    contentWidth?: string;
    status?: ContentStatusEnum;
    pageTemplate?: Type<any>;
    persistedStatus?: ContentStatusEnum;
    hasUnsavedChanges?: boolean;
}
export class Page {
    pageId: string;
    pageName: string;
    pageType: PageType;
    pageLevel: PageLevel;
    sections: Section[];
    contentWidth: string;
    status: ContentStatusEnum;
    pageTemplate: Type<any>
    // Add these properties to track workflow status
    persistedStatus?: ContentStatusEnum; // The last saved status
    hasUnsavedChanges?: boolean; // Quick flag for UI

    constructor(options: PageOptions = {}) {
        this.pageId = options.pageId;
        this.pageName = options.pageName;
        this.pageType = options.pageType;
        this.pageLevel = options.pageLevel;
        this.contentWidth = options.contentWidth;
        this.sections = options.sections;
        this.status = options.status;
        this.pageTemplate = options.pageTemplate;
        // Initialize persisted status to current status
        this.persistedStatus = options.persistedStatus ?? options.status;
        this.hasUnsavedChanges = options.hasUnsavedChanges ?? false;
    }

    getPageId() {
        return this.pageId
    }

    getPageName() {
        return this.pageId
    }
    getPageType() {
        return this.pageId
    }
    getPageLevel() {
        return this.pageId
    }
    getSections() {
        return this.pageId
    }

    getPageTemplate() {
        return this.pageTemplate;
    }

    setPageTemplate(pageTemplate: Type<any>) {
        this.pageTemplate = pageTemplate;
    }

    setSections(sections: Section[]) {
        this.sections = sections;
    }

    setContentWidth(contentWidth: string) {
        this.contentWidth = contentWidth;
    }

    getContentWidth() {
        return this.contentWidth;
    }
    getStatus() {
        return this.status;
    }

    getPersistedStatus() {
        return this.persistedStatus;
    }

    getHasUnsavedChanges() {
        return this.hasUnsavedChanges;
    }

    // Mark page as modified
    markAsModified() {
        this.hasUnsavedChanges = true;
    }

    // Call this after successful save
    markAsSaved() {
        this.persistedStatus = this.status;
        this.hasUnsavedChanges = false;
    }

}

export interface SiteEditorConfig {
    sites?: Site[];
}

export interface SiteMataData {
    siteName: string;
    siteId: string;
    themeName: string;
    color?: string;
}

export interface MenuItemContext {
    elId?: string;
    cellId?: string;
    contentElement?: ContentElement;
    cell?: GridCell | RowCell;
    target?: string // Layout or Theme - Default: theme
}

